import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync, FaArrowUp, FaArrowDown, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const initialBannerState = {
  id: null,
  heading: '',
  content_html: '',
  is_active: true
};

const BannerContentEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-gray-50 text-gray-500 text-sm">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '160px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const BannerManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState(initialBannerState);
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  const [savingBanner, setSavingBanner] = useState(false);

  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const normalize = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const loadBanners = useCallback(async () => {
    const params = {};
    if (search && search.trim()) params.search = search.trim();
    if (filterActive === 'active') params.is_active = '1';
    if (filterActive === 'inactive') params.is_active = '0';

    const response = await api.banners.list(params);
    if (response.data?.success) {
      setBanners(response.data.data || []);
    }
  }, [search, filterActive]);

  const fetchAll = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);
      await loadBanners();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [loadBanners]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchAll({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchAll, loading, refreshing]);

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [banners]);

  const handleBannerFormChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setBannerForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleBannerContentChange = useCallback((html) => {
    setBannerForm((prev) => ({
      ...prev,
      content_html: html || ''
    }));
  }, []);

  const handleNewBanner = useCallback(() => {
    setSelectedBannerId(null);
    setBannerForm(initialBannerState);
  }, []);

  const handleEditBanner = useCallback((banner) => {
    if (!banner) return;
    setSelectedBannerId(banner.id);
    setBannerForm({
      id: banner.id,
      heading: banner.heading || '',
      content_html: banner.content_html || '',
      is_active: !!banner.is_active
    });
  }, []);

  const handleSaveBanner = useCallback(async (event) => {
    event.preventDefault();

    const heading = normalize(bannerForm.heading);
    const content_html = bannerForm.content_html || '';
    if (!heading || !content_html.trim()) return;

    setSavingBanner(true);
    try {
      const payload = {
        heading,
        content_html,
        is_active: !!bannerForm.is_active
      };

      if (bannerForm.id) {
        await api.banners.update(bannerForm.id, payload);
      } else {
        await api.banners.create(payload);
      }

      await loadBanners();
      handleNewBanner();
    } finally {
      setSavingBanner(false);
    }
  }, [bannerForm, handleNewBanner, loadBanners, normalize]);

  const handleDeleteBanner = useCallback(async (bannerId) => {
    if (!bannerId) return;
    await api.banners.remove(bannerId);
    if (selectedBannerId === bannerId) {
      handleNewBanner();
    }
    await loadBanners();
  }, [handleNewBanner, loadBanners, selectedBannerId]);

  const changeBannerOrder = useCallback(async (bannerId, direction) => {
    const items = sortedBanners.map((b) => ({ id: b.id, position: b.position }));
    const index = items.findIndex((x) => x.id === bannerId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const tmp = items[index - 1].position;
      items[index - 1].position = items[index].position;
      items[index].position = tmp;
    }
    if (direction === 'down' && index < items.length - 1) {
      const tmp = items[index + 1].position;
      items[index + 1].position = items[index].position;
      items[index].position = tmp;
    }

    await api.banners.reorder(items);
    await loadBanners();
  }, [loadBanners, sortedBanners]);

  // Re-load when filters change
  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Banner Management</h3>
            <p className="text-sm text-gray-600 mt-1">Manage banners with heading and rich text content for landing or dashboard pages.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewBanner}
            disabled={savingBanner}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="inline-block mr-2" /> New Banner
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Search</label>
            <div className="flex items-center bg-white border border-purple-200 rounded-lg px-3 py-2">
              <FaSearch className="text-gray-400 mr-2" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search headings or content..." className="w-full outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
            <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h6 className="text-lg font-bold mb-3">Banners</h6>
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4">Heading</th>
                    <th className="text-center py-3 px-4" style={{ width: '120px' }}>Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-purple-50">
                  {sortedBanners.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-gray-500 py-6">No banners found. Use the editor below to create your first banner.</td>
                    </tr>
                  ) : (
                    sortedBanners.map((banner, index) => (
                      <tr key={banner.id}>
                        <td className="py-3 px-4 align-top">
                          <button onClick={() => handleEditBanner(banner)} className="text-left text-sm font-medium text-purple-800 hover:underline">{banner.heading}</button>
                        </td>
                        <td className="py-3 px-3 text-left">
                          {banner.is_active ? (
                            <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Inactive</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeBannerOrder(banner.id, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowUp className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeBannerOrder(banner.id, 'down')}
                              disabled={index === sortedBanners.length - 1}
                              title="Move Down"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowDown className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleDeleteBanner(banner.id)}
                              title="Delete Banner"
                              className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                            >
                              <FaTrash className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <h6 className="text-lg font-bold mb-3">{selectedBannerId ? 'Edit Banner' : 'New Banner'}</h6>
          <form onSubmit={handleSaveBanner} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Heading</label>
              <input type="text" name="heading" value={bannerForm.heading} onChange={handleBannerFormChange} disabled={savingBanner} placeholder="Banner heading" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_active" checked={bannerForm.is_active} onChange={handleBannerFormChange} disabled={savingBanner} className="w-4 h-4" />
                <span>Active</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <BannerContentEditor value={bannerForm.content_html} onChange={handleBannerContentChange} disabled={savingBanner} />
              <p className="text-xs text-gray-500 mt-2">Use this rich text editor to describe the banner content. HTML is stored and rendered on the public view.</p>
            </div>

            <div className="flex justify-end items-center gap-3">
              {selectedBannerId && (
                <Button
                  bsPrefix="custom-hamburger"
                  type="button"
                  onClick={handleNewBanner}
                  disabled={savingBanner}
                  className="border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                >
                  Cancel
                </Button>
              )}
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                disabled={savingBanner}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2 flex items-center gap-2"
              >
                {savingBanner ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save Banner
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BannerManager;
